import styled, { css, keyframes } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';

const iconSize = size(3);
const textSize = '0.875rem';
const textLineHeight = '1.5';

export const IconBadge = styled.div.withConfig({ componentId: 'sc-usm8td-0' })`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${iconSize};
    height: ${iconSize};
    margin-top: calc((${iconSize} - ${textSize} * ${textLineHeight}) / -2);
    border-radius: 50%;
    font-size: 1rem;
`;

interface WrapperProps {
  $tone?: 'success' | 'warning' | 'error' | 'info';
}

export const Wrapper = styled.div.withConfig({ componentId: 'sc-usm8td-1' })<WrapperProps>`
    --alert-bg: var(--c-semantic-neutral-300);
    --alert-fg: var(--c-text-main);
    --alert-icon-bg: var(--c-semantic-neutral-500);
    --alert-icon-fg: var(--c-semantic-neutral-100);

    display: flex;
    padding: ${size(2)} ${size(3)};
    background-color: var(--alert-bg);
    color: var(--alert-fg);
    border-radius: ${(props) => props.theme.size.borderRadius};

    ${IconBadge} {
        background-color: var(--alert-icon-bg);
        color: var(--alert-icon-fg);
    }

    ${({ $tone }) =>
      $tone === 'success'
        ? css`
                  --alert-bg: var(--c-semantic-success-100);
                  --alert-icon-bg: var(--c-semantic-success-300);
                  --alert-icon-fg: var(--c-semantic-success-700);
              `
        : $tone === 'warning'
          ? css`
                  --alert-bg: var(--c-semantic-warning-100);
                  --alert-icon-bg: var(--c-semantic-warning-300);
                  --alert-icon-fg: var(--c-semantic-warning-700);
              `
          : $tone === 'error'
            ? css`
                  --alert-bg: var(--c-semantic-error-100);
                  --alert-icon-bg: var(--c-semantic-error-300);
                  --alert-icon-fg: var(--c-semantic-error-700);
              `
            : $tone === 'info'
              ? css`
                  --alert-bg: var(--c-semantic-info-100);
                  --alert-icon-bg: var(--c-semantic-info-300);
                  --alert-icon-fg: var(--c-semantic-info-700);
              `
              : undefined}
`;

export const Icon = styled.div.withConfig({ componentId: 'sc-usm8td-2' })`
    flex-shrink: 0;
    padding-right: ${size(1.5)};
`;

export const Main = styled.div.withConfig({ componentId: 'sc-usm8td-3' })`
    flex: 1 1 auto;
    font-size: ${textSize};
    line-height: ${textLineHeight};
`;

export const Heading = styled.div.withConfig({ componentId: 'sc-usm8td-4' })`
    font-weight: var(--font-weight-medium);
    margin-bottom: ${size(1)};
`;

export const FloatingAlertWrapper = styled.div.withConfig({ componentId: 'sc-usm8td-5' })`
    position: fixed;
    inset: 0;
    padding: ${(props) => props.theme.size.containerGutterLtMd};
    z-index: ${(props) => props.theme.zIndex.floatingAlert};

    ${(props) => props.theme.breakpoint.Md} {
        padding: ${(props) => props.theme.size.containerGutter};
    }
`;

export const FloatingInner = styled.div.withConfig({ componentId: 'sc-usm8td-6' })`
    position: relative;
    z-index: 1;
    max-width: calc(
        ${(props) => props.theme.size.containerMaxWidth} - (${(props) => props.theme.size.containerGutter} * 2)
    );
    margin: 0 auto;
    animation: ${keyframes`
        from {
            transform: scale(0.95);
            opacity: 0.5;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    `} 150ms linear;
`;

export const CloseButton = styled(ButtonReset).withConfig({ componentId: 'sc-usm8td-7' })`
    position: absolute;
    top: ${size(2)};
    right: ${size(2)};
    z-index: 50;
`;

export const UiBlock = styled.div.withConfig({ componentId: 'sc-usm8td-8' })`
    background-color: rgba(0, 0, 0, 0.25);
    position: fixed;
    inset: 0;
    animation: ${keyframes`
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    `} 150ms linear;
`;
